import { ANOMALY_EFFECTS } from './anomaly-effect.js';
/**
 * ⚠️ **Depuis #412, le tableau des eaux pluviales** (`outdoorElements`, `survey-matrix.ts`) remplace
 * cette carte : une case « Réseau EU » remplie est une anomalie, une case « Réseau unitaire » suit
 * la règle « collecte non séparative » du client. La carte reste lue pour les relevés qui la
 * portent — leur lecture et leur conclusion ne bougent pas.
 *
 * **Eaux de pluie vers les eaux usées** — la carte « Surfaces raccordées » du prototype validé,
 * renommée le 01/10/2026 (arbitrage du développeur, après test d'un scénario de contrôle), avec la
 * logique de GHP DIGIT : son cadre « Anomalies relevées EP vers EU » (04_fiche_controle § 2.2,
 * cadre 6). Une ligne cochée est une **anomalie** : de la pluie (toit, rampe, cour) envoyée au
 * réseau d'eaux usées, avec la surface qui s'y écoule.
 *
 * ⚠️ **La matrice 8 × 9 de la fiche papier n'est pas saisie** (arbitrage du 01/10/2026) : le
 * prototype et DIGIT décrivent ce fait comme ici, et c'est la seule forme qu'un agent ait utilisée.
 */
/**
 * Les deux **paires de côtés** du bâtiment : une surface pour la paire, une case par côté.
 *
 * ⚠️ **La forme de l'application tablette d'avant** (retour du développeur, 23/09) : DIGIT porte
 * aussi une surface pour l'arrière, une pour l'avant (`surfGoutAr`, `surfGoutAv`).
 */
export const SURFACE_PAIRS = [
    { key: 'rear', sides: ['rear_left', 'rear_right'] },
    { key: 'front', sides: ['front_left', 'front_right'] },
];
/** Les surfaces qui ne sont pas des côtés : une case et une surface chacune. */
export const SINGLE_SURFACES = ['garage_ramp', 'yard', 'drain', 'other'];
/**
 * Tout ce qu'une ligne de `connectedSurfaces` peut nommer, dans l'ordre de la fiche.
 *
 * ⚠️ **Des constantes, pas une liste de `vocabulary`** : ce sont les côtés d'une parcelle, pas
 * un référentiel que le bureau administre.
 */
export const CONNECTED_SURFACE_SIDES = [
    'rear',
    'front',
    'rear_left',
    'rear_right',
    'front_left',
    'front_right',
    ...SINGLE_SURFACES,
];
/**
 * Ce que chaque ligne s'appelle, **dans les mots de ce que l'agent voit** — « Gouttières
 * arrière : gauche / droite » et non « Ar. G » : le scénario de test du 01/10 a montré qu'une
 * abréviation ne se relie pas à ce qu'on constate devant la maison.
 */
export const CONNECTED_SURFACE_LABELS = {
    rear: 'Gouttières arrière',
    front: 'Gouttières avant',
    rear_left: 'gauche',
    rear_right: 'droite',
    front_left: 'gauche',
    front_right: 'droite',
    garage_ramp: 'Descente de garage',
    yard: 'Cour ou voie privée',
    drain: 'Drain',
    other: 'Autres',
};
/** Le nom complet d'un côté : ce qu'un lecteur d'écran annonce sur sa case. */
export const CONNECTED_SURFACE_FULL_LABELS = {
    ...CONNECTED_SURFACE_LABELS,
    rear_left: 'Gouttière arrière gauche',
    rear_right: 'Gouttière arrière droite',
    front_left: 'Gouttière avant gauche',
    front_right: 'Gouttière avant droite',
};
/** Sous chaque ligne : ce qu'on voit sur place, et ce que mesure la surface. */
const CONNECTED_SURFACE_HINTS = {
    rear: 'Les descentes de la toiture arrière — surface : la toiture arrière',
    front: 'Les descentes de la toiture avant — surface : la toiture avant',
    garage_ramp: 'La grille au pied de la rampe — surface : la rampe',
    yard: 'La grille ou le caniveau de la cour — surface : la cour',
    drain: 'Le drain autour de la maison',
    other: 'Toute autre arrivée d’eau de pluie',
};
/**
 * Une surface **pour mémoire** ou **inconnue**, à la place d'un nombre — le « PM » et le « ? » que
 * GHP DIGIT acceptait dans le champ (03_anomalies, RG-F11). Ni l'une ni l'autre ne compte au total.
 */
export const AREA_NOTES = ['memo', 'unknown'];
export const AREA_NOTE_LABELS = {
    memo: 'PM',
    unknown: '?',
};
/**
 * Les lignes, dans l'ordre de la fiche : les deux paires, puis les surfaces simples, puis les
 * côtés inconnus — gardés et montrés par leur code (M21). `all` : toutes les lignes, relevées ou
 * non (la tablette) ; sinon ce qui a été relevé (le bureau, le rapport).
 *
 * ⚠️ **Un relevé d'avant le 23/09** porte une surface par côté et aucune sur la paire : la surface
 * de la paire est alors la somme de ses côtés mesurés.
 */
export function connectedSurfaceRows(surfaces, { all = false } = {}) {
    const entries = surfaces ?? [];
    const entry = (side) => entries.find((surface) => surface.side === side);
    const known = new Set(CONNECTED_SURFACE_SIDES);
    const rows = [];
    for (const pair of SURFACE_PAIRS) {
        const own = entry(pair.key);
        const sides = pair.sides.map((side) => ({
            side,
            label: CONNECTED_SURFACE_LABELS[side],
            fullLabel: CONNECTED_SURFACE_FULL_LABELS[side],
            connected: entry(side)?.connected === true,
        }));
        const legacyAreas = pair.sides
            .map((side) => entry(side)?.areaM2)
            .filter((area) => area != null);
        // La ligne de la paire, dès qu'elle existe, fait foi — même vidée : sinon effacer la surface
        // ferait réapparaître celle des côtés.
        const areaM2 = own !== undefined
            ? (own.areaM2 ?? null)
            : legacyAreas.length === 0
                ? null
                : legacyAreas.reduce((sum, area) => sum + area, 0);
        const seen = own !== undefined || pair.sides.some((side) => entry(side) !== undefined);
        if (all || seen) {
            rows.push({
                key: pair.key,
                label: CONNECTED_SURFACE_LABELS[pair.key],
                hint: CONNECTED_SURFACE_HINTS[pair.key] ?? null,
                sides,
                connected: sides.some((side) => side.connected),
                areaM2,
                areaNote: own?.areaNote ?? null,
            });
        }
    }
    for (const single of SINGLE_SURFACES) {
        const own = entry(single);
        if (all || own !== undefined) {
            rows.push({
                key: single,
                label: CONNECTED_SURFACE_LABELS[single],
                hint: CONNECTED_SURFACE_HINTS[single] ?? null,
                sides: [],
                connected: own?.connected === true,
                areaM2: own?.areaM2 ?? null,
                areaNote: own?.areaNote ?? null,
            });
        }
    }
    for (const unknown of entries.filter((surface) => !known.has(surface.side))) {
        rows.push({
            key: unknown.side,
            label: unknown.side,
            hint: null,
            sides: [],
            connected: unknown.connected === true,
            areaM2: unknown.areaM2 ?? null,
            areaNote: unknown.areaNote ?? null,
        });
    }
    return rows;
}
/**
 * **Le total raccordé, en m²** — la somme des lignes cochées et mesurées. Une ligne décochée ne
 * compte pas, même mesurée ; une surface « pour mémoire » ou « inconnue » non plus.
 */
export function connectedSurfacesTotal(surfaces) {
    return connectedSurfaceRows(surfaces)
        .filter((row) => row.connected && row.areaNote === null)
        .reduce((total, row) => total + (row.areaM2 ?? 0), 0);
}
/** La surface d'une ligne telle qu'elle se dit et s'imprime. */
export function connectedSurfaceArea(row) {
    if (row.areaNote !== null)
        return AREA_NOTE_LABELS[row.areaNote] ?? row.areaNote;
    return row.areaM2 === null ? null : `${AREA.format(row.areaM2)} m²`;
}
const AREA = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 });
/**
 * **Où partent les eaux pluviales** — les six cases du cadre « Raccordement Eaux Pluviales » de
 * GHP DIGIT (04_fiche_controle § 2.2, cadre 5), sans effet sur la conclusion.
 */
export const STORMWATER_OUTLETS = [
    'gargoyles',
    'trap',
    'spreading',
    'soakaway',
    'ditch_or_stormwater_network',
    'on_ground',
];
export const STORMWATER_OUTLET_LABELS = {
    gargoyles: 'Gargouilles',
    trap: 'Tabouret',
    spreading: 'Épandage',
    soakaway: 'Puits perdu',
    ditch_or_stormwater_network: 'Fossé ou réseau EP',
    on_ground: 'Sur sol',
};
/** « Tabouret, Fossé ou réseau EP » ; `null` quand rien n'est relevé. Un code inconnu passe tel quel. */
export function stormwaterOutletsSummary(survey) {
    const outlets = (survey.stormwaterOutlets ?? []).map((code) => STORMWATER_OUTLET_LABELS[code] ?? code);
    const parts = [
        outlets.length === 0 ? null : outlets.join(', '),
        survey.stormwaterUndetermined === true ? 'destination indéterminée pour tout ou partie' : null,
    ].filter((part) => part !== null);
    return parts.length === 0 ? null : parts.join(' · ');
}
/**
 * **Les anomalies « eaux de pluie »**, dérivées du relevé, jamais stockées : la liste d'anomalies,
 * la conclusion et le rapport le lisent. La phrase de travaux de DIGIT (« Séparer les EP des
 * EU… ») est proposée dans « Travaux à réaliser » par `suggestRecommendations` (#345).
 *
 * - **Le tableau** (#412) : une case « Réseau EU » remplie est une anomalie — « Déconnecter GTT
 *   AVANT DROITE du réseau d’eaux usées (constaté : 2 gouttières, 46 m²) » ; une case « Réseau
 *   unitaire » suit l'effet figé de la règle « collecte non séparative » du client : une anomalie
 *   en non-conformité ou sans règle (#413), une mention en dérogation, rien en conforme ou sans
 *   objet.
 * - **La carte d'avant** : une ligne cochée est une anomalie, sauf en réseau unitaire (GHP5) ; un
 *   réseau non renseigné suit le séparatif.
 *
 * `vocabulary` nomme les éléments du tableau ; sans lui, leur code.
 */
export function stormwaterAnomalies(survey, vocabulary = []) {
    const legacy = survey.plot?.publicNetworkType === 'combined'
        ? []
        : connectedSurfaceRows(survey.connectedSurfaces)
            .filter((row) => row.connected)
            .map((row) => ({
            key: row.key,
            sentence: `Déconnecter ${connectedSurfaceName(row).toLocaleUpperCase('fr-FR')} du réseau d’eaux usées (constaté : eaux de pluie vers les EU, ${measureOf(row)})`,
            effect: null,
        }));
    const labelOf = (code) => (vocabulary.find((entry) => entry.list === 'outdoor_element' && entry.code === code)?.label ??
        code).toLocaleUpperCase('fr-FR');
    const table = (survey.outdoorElements ?? []).flatMap((cell) => {
        const key = `${cell.element}/${cell.destination}`;
        const observed = observedOf(cell);
        if (cell.destination === 'RESEAU_EU') {
            return [
                {
                    key,
                    sentence: `Déconnecter ${labelOf(cell.element)} du réseau d’eaux usées (constaté : ${observed})`,
                    effect: null,
                },
            ];
        }
        const effect = ANOMALY_EFFECTS.find((known) => known === cell.effect) ?? null;
        if (cell.destination !== 'RESEAU_UNITAIRE' ||
            effect === 'accepted' ||
            effect === 'not_applicable') {
            return [];
        }
        return [
            {
                key,
                sentence: `Collecte non séparative : ${labelOf(cell.element)} vers le réseau unitaire (constaté : ${observed})`,
                effect,
            },
        ];
    });
    return [...legacy, ...table];
}
/** « 2 gouttières, 46 m² » — le nombre puis la surface d'une case du tableau. */
function observedOf(cell) {
    const count = cell.count ?? null;
    const counted = count === null
        ? null
        : cell.element.startsWith('GTT_')
            ? `${String(count)} gouttière${count > 1 ? 's' : ''}`
            : `nombre ${String(count)}`;
    return [counted, areaOf(cell.areaM2 ?? null, cell.areaNote ?? null)]
        .filter((part) => part !== null)
        .join(', ');
}
/**
 * **Ce que la transmission rappelle des eaux de pluie** (M21 : rien ne bloque) : une case « Réseau
 * EU » ou « Réseau unitaire » sans surface ni note laisse le rapport sans mesure. Pour la carte
 * d'avant, un réseau public non renseigné fait aussi compter ses lignes comme en séparatif.
 */
export function stormwaterWarnings(survey, vocabulary = []) {
    const unmeasured = (survey.outdoorElements ?? [])
        .filter((cell) => (cell.destination === 'RESEAU_EU' || cell.destination === 'RESEAU_UNITAIRE') &&
        cell.areaM2 == null &&
        cell.areaNote == null)
        .map((cell) => {
        const name = (list, code) => vocabulary.find((entry) => entry.list === list && entry.code === code)?.label ?? code;
        return `${name('outdoor_element', cell.element)} → ${name('outdoor_destination', cell.destination)} : surface non mesurée — saisissez-la, ou notez « PM » ou « ? ».`;
    });
    const connected = connectedSurfaceRows(survey.connectedSurfaces).filter((row) => row.connected);
    if (connected.length === 0)
        return unmeasured;
    return [
        ...(survey.plot?.publicNetworkType == null
            ? [
                'Réseau public non renseigné, dans « Desserte de réseau EU » : les eaux de pluie vers les eaux usées comptent comme des anomalies, ce qui est faux en réseau unitaire.',
            ]
            : []),
        ...connected
            .filter((row) => row.areaM2 === null && row.areaNote === null)
            .map((row) => `${connectedSurfaceName(row)} : surface non mesurée — saisissez-la, ou notez « PM » ou « ? ».`),
        ...unmeasured,
    ];
}
/**
 * Le nom d'une ligne tel que l'anomalie, le bureau et le rapport le disent : « Gouttières arrière
 * gauche et droite » pour une paire — ses côtés cochés —, le libellé seul sinon.
 */
export function connectedSurfaceName(row) {
    const sides = row.sides.filter((side) => side.connected).map((side) => side.label);
    return sides.length === 0 ? row.label : `${row.label} ${sides.join(' et ')}`;
}
function measureOf(row) {
    return areaOf(row.areaM2, row.areaNote);
}
function areaOf(areaM2, areaNote) {
    if (areaNote === 'memo')
        return 'surface pour mémoire';
    if (areaNote === 'unknown')
        return 'surface inconnue';
    return areaM2 === null ? 'surface non mesurée' : `${AREA.format(areaM2)} m²`;
}
//# sourceMappingURL=stormwater.js.map